import { BrowserProvider, Contract, type JsonRpcSigner } from 'ethers';
import type { Address } from 'viem';
import { disconnect, getAccount, reconnect, switchChain, watchAccount } from '@wagmi/core';

import { getActiveChainConfig } from './config';
import { applyReownLightMode, reownModal, wagmiConfig } from './reown';

export type WalletState = {
  provider: BrowserProvider | null;
  signer: JsonRpcSigner | null;
  account: Address | null;
  chainId: number | null;
};

type Eip1193Provider = {
  request(args: { method: string; params?: unknown[] | object }): Promise<any>;
  on?(event: string, listener: (...args: any[]) => void): void;
  removeListener?(event: string, listener: (...args: any[]) => void): void;
};

export const walletState: WalletState = {
  provider: null,
  signer: null,
  account: null,
  chainId: null,
};

let switchModalEl: HTMLDivElement | null = null;

function clearWalletState(): WalletState {
  walletState.provider = null;
  walletState.signer = null;
  walletState.account = null;
  walletState.chainId = null;
  return walletState;
}

async function syncWalletState(): Promise<WalletState> {
  const account = getAccount(wagmiConfig);
  if (account.status !== 'connected' || !account.address || !account.connector) {
    return clearWalletState();
  }

  const provider = (await account.connector.getProvider({ chainId: account.chainId })) as Eip1193Provider | null;
  if (!provider) {
    return clearWalletState();
  }

  const browserProvider = new BrowserProvider(provider as any);
  const signer = await browserProvider.getSigner(account.address);

  walletState.provider = browserProvider;
  walletState.signer = signer;
  walletState.account = account.address as Address;
  walletState.chainId = account.chainId;

  return walletState;
}

async function waitForModalConnection(timeoutMs = 240000): Promise<WalletState> {
  const current = getAccount(wagmiConfig);
  if (current.status === 'connected' && current.address) {
    return syncWalletState();
  }

  return new Promise<WalletState>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      settled = true;
      stopWatch();
      stopModal();
      window.clearTimeout(timer);
    };

    const resolveConnected = async () => {
      try {
        const state = await syncWalletState();
        cleanup();
        resolve(state);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    const stopWatch = watchAccount(wagmiConfig, {
      onChange(account) {
        if (!settled && account.status === 'connected' && account.address) {
          void resolveConnected();
        }
      },
    });

    const stopModal = reownModal.subscribeState((state) => {
      if (settled || state.open) return;
      const account = getAccount(wagmiConfig);
      if (account.status !== 'connected') {
        cleanup();
        reject(new Error('Wallet connection canceled'));
      }
    });

    const timer = window.setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error('Wallet connection timed out'));
    }, timeoutMs);
  });
}

export async function connectWallet(): Promise<WalletState> {
  applyReownLightMode();
  const pending = waitForModalConnection();
  await reownModal.open({ view: 'Connect' });
  return pending;
}

export async function switchToPulseNetwork(): Promise<void> {
  const chain = getActiveChainConfig();
  await switchChain(wagmiConfig, { chainId: chain.id });
}

function closeSwitchModal(): void {
  if (switchModalEl) {
    switchModalEl.remove();
    switchModalEl = null;
  }
}

function openSwitchModal(onSwitch: () => Promise<void>): Promise<boolean> {
  closeSwitchModal();

  const chain = getActiveChainConfig();
  const overlay = document.createElement('div');
  overlay.className = 'network-modal-overlay';
  overlay.innerHTML = `
    <div class="network-modal card" role="dialog" aria-modal="true" aria-label="Switch network">
      <div class="network-modal-title">Switch Network</div>
      <div class="network-modal-body">This app runs on ${chain.chainName}. Switch your wallet network to continue.</div>
      <div class="network-modal-actions">
        <button type="button" class="btn" data-action="cancel">Cancel</button>
        <button type="button" class="btn btn-primary" data-action="switch">Switch to ${chain.chainName}</button>
      </div>
    </div>
  `;

  switchModalEl = overlay;
  document.body.appendChild(overlay);

  return new Promise<boolean>((resolve) => {
    const cleanup = (value: boolean) => {
      closeSwitchModal();
      resolve(value);
    };

    const cancelBtn = overlay.querySelector('[data-action="cancel"]') as HTMLButtonElement | null;
    const switchBtn = overlay.querySelector('[data-action="switch"]') as HTMLButtonElement | null;

    cancelBtn?.addEventListener('click', () => cleanup(false));
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) cleanup(false);
    });

    switchBtn?.addEventListener('click', async () => {
      if (!switchBtn) return;
      switchBtn.disabled = true;
      switchBtn.textContent = 'Switching...';

      try {
        await onSwitch();
        cleanup(true);
      } catch {
        switchBtn.disabled = false;
        switchBtn.textContent = `Switch to ${chain.chainName}`;
      }
    });
  });
}

export async function ensurePulseChainConnected(): Promise<boolean> {
  if (!walletState.account) {
    try {
      await reconnect(wagmiConfig);
      await syncWalletState();
      if (!walletState.account) return false;
    } catch {
      return false;
    }
  }

  const chain = getActiveChainConfig();
  if (walletState.chainId === chain.id) return true;

  const switched = await openSwitchModal(async () => {
    await switchToPulseNetwork();
    await syncWalletState();
  });

  return switched && walletState.chainId === chain.id;
}

export async function rehydrateWalletSession(): Promise<WalletState> {
  try {
    await reconnect(wagmiConfig);
  } catch {
    return clearWalletState();
  }

  return syncWalletState();
}

export function bindWalletEvents(onChange: () => void | Promise<void>): () => void {
  return watchAccount(wagmiConfig, {
    async onChange(account) {
      try {
        if (account.status === 'connected' && account.address) {
          await syncWalletState();
        } else {
          clearWalletState();
        }
      } catch {
        clearWalletState();
      } finally {
        await onChange();
      }
    },
  });
}

export function disconnectWalletSession(): WalletState {
  void disconnect(wagmiConfig).catch(() => undefined);
  return clearWalletState();
}

export function requireSigner(): JsonRpcSigner {
  const chain = getActiveChainConfig();
  if (!walletState.signer || !walletState.account) throw new Error('Connect wallet first');
  if (walletState.chainId !== chain.id) throw new Error(`Switch to ${chain.chainName} first`);
  return walletState.signer;
}

export function contractWithSigner(address: string, abi: readonly string[]) {
  const signer = requireSigner();
  return new Contract(address, abi, signer);
}
